import React, { useEffect, useState } from "react";
import Sidebar from "../Layout/Sidebar";
import Header from "../Layout/Header";
import axios from "axios";
import Swal from "sweetalert2";
import { API_BASE } from "../../../ApiUrl";
import { useNavigate, useParams } from "react-router-dom";
import { FiSave, FiX, FiArrowLeft } from "react-icons/fi";
import "./AddScopeCatalog.css";

function AddScopeCatalog() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = Boolean(id);

  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(isEditMode);
  const [industries, setIndustries] = useState([]);
  const [originalRecord, setOriginalRecord] = useState(null);

  const [formData, setFormData] = useState({
    code: "",
    scope_name: "",
    description: "",
    competency_focus: "",
    suggested_resume_section: "",
    is_active: true,
    industry: "",
  });

  useEffect(() => {
    fetchIndustries();
    if (isEditMode) {
      fetchScope();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchIndustries = async () => {
    try {
      const res = await axios.get(`${API_BASE}/reference-values/`);
      const allValues = res.data?.data || [];
      const industryOnly = allValues
        .filter((v) => v.option_set === "INDUSTRY")
        .sort((a, b) => a.sort_order - b.sort_order || a.label.localeCompare(b.label));
      setIndustries(industryOnly);
    } catch (error) {
      console.error("Error fetching industries:", error);
      Swal.fire("Error", "Failed to load industry options", "error");
    }
  };

  const fetchScope = async () => {
    setFetching(true);
    try {
      const res = await axios.get(`${API_BASE}/scope-catalogs/${id}/`);
      const item = res.data?.data || res.data;
      setOriginalRecord(item);
      setFormData({
        code: item.code || "",
        scope_name: item.scope_name || "",
        description: item.description || "",
        competency_focus: item.competency_focus || "",
        suggested_resume_section: item.suggested_resume_section || "",
        is_active: item.is_active ?? true,
        industry: item.industry || "",
      });
    } catch (error) {
      console.error("Error fetching scope catalog:", error);
      Swal.fire("Error", "Failed to load scope details", "error");
      navigate("/catalog/scope-catalogs");
    } finally {
      setFetching(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const toggleActive = () => {
    setFormData((prev) => ({ ...prev, is_active: !prev.is_active }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (
      !formData.code.trim() ||
      !formData.scope_name.trim() ||
      !formData.industry
    ) {
      Swal.fire(
        "Validation Error",
        "Code, Scope Name and Industry are required",
        "warning"
      );
      return;
    }

    setLoading(true);
    try {
      if (isEditMode) {
        // PUT expects the full object — merge original record with edited fields
        const payload = {
          code: formData.code.trim(),
          scope_name: formData.scope_name.trim(),
          description: formData.description.trim(),
          competency_focus: formData.competency_focus.trim(),
          suggested_resume_section: formData.suggested_resume_section.trim(),
          activation_rule: originalRecord?.activation_rule || "",
          version: originalRecord?.version ?? 1,
          status: originalRecord?.status || "DRAFT",
          is_active: formData.is_active,
          industry: Number(formData.industry),
          created_by: originalRecord?.created_by ?? 1,
        };
        await axios.put(`${API_BASE}/scope-catalogs/${id}/`, payload);
        Swal.fire({
          icon: "success",
          title: "Updated!",
          text: "Scope catalog entry has been updated.",
          timer: 2000,
          showConfirmButton: false,
        });
      } else {
        const payload = {
          code: formData.code.trim(),
          scope_name: formData.scope_name.trim(),
          description: formData.description.trim(),
          competency_focus: formData.competency_focus.trim(),
          suggested_resume_section: formData.suggested_resume_section.trim(),
          activation_rule: "",
          version: 1,
          status: "DRAFT",
          is_active: formData.is_active,
          industry: Number(formData.industry),
          created_by: 1,
        };
        await axios.post(`${API_BASE}/scope-catalogs/`, payload);
        Swal.fire({
          icon: "success",
          title: "Added!",
          text: "Scope catalog entry has been created.",
          timer: 2000,
          showConfirmButton: false,
        });
      }
      navigate("/catalog/scope-catalogs");
    } catch (error) {
      console.error(
        `Error ${isEditMode ? "updating" : "creating"} scope catalog:`,
        error
      );
      Swal.fire(
        "Error",
        error?.response?.data?.message ||
        `Failed to ${isEditMode ? "update" : "create"} scope catalog`,
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate(-1);
  };

  if (fetching) {
    return (
      <div className="app-layout">
        <Sidebar />
        <div className="main-content">
          <Header />
          <div className="page-content">
            <div className="loader-container">
              <div className="loader">
                <div className="spinner"></div>
                <p>Loading scope catalog...</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="app-layout">
      <Sidebar />

      <div className="main-content">
        <Header />

        <div className="page-content">

          <div className="scope-form-top">
            <button
              type="button"
              className="scope-back-btn"
              onClick={handleBack}
              title="Back"
            >
              <FiArrowLeft />
            </button>
            <div className="scope-form-page-heading">
              <h3>{isEditMode ? "Edit Scope Catalog" : "Add Scope Catalog"}</h3>
            </div>
          </div>
          <div className="scope-form-card">


            <form onSubmit={handleSubmit}>
              <div className="scope-form-grid">
                <div className="scope-field">
                  <label htmlFor="code">
                    Code <span className="required">*</span>
                  </label>
                  <input
                    type="text"
                    id="code"
                    name="code"
                    placeholder="SC001"
                    value={formData.code}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="scope-field">
                  <label htmlFor="scope_name">
                    Scope Name <span className="required">*</span>
                  </label>
                  <input
                    type="text"
                    id="scope_name"
                    name="scope_name"
                    placeholder="Pipeline Integrity Assessment"
                    value={formData.scope_name}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="scope-field">
                  <label htmlFor="industry">
                    Industry <span className="required">*</span>
                  </label>
                  <select
                    id="industry"
                    name="industry"
                    value={formData.industry}
                    onChange={handleChange}
                    required
                  >
                    <option value="">Select industry</option>
                    {industries.map((ind) => (
                      <option key={ind.id} value={ind.id}>
                        {ind.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="scope-field">
                  <label htmlFor="competency_focus">Competency Focus</label>
                  <input
                    type="text"
                    id="competency_focus"
                    name="competency_focus"
                    placeholder="Structural inspection"
                    value={formData.competency_focus}
                    onChange={handleChange}
                  />
                </div>

                <div className="scope-field">
                  <label htmlFor="suggested_resume_section">
                    Suggested Resume Section
                  </label>
                  <input
                    type="text"
                    id="suggested_resume_section"
                    name="suggested_resume_section"
                    placeholder="Technical Experience"
                    value={formData.suggested_resume_section}
                    onChange={handleChange}
                  />
                </div>

                <div className="scope-field scope-field-full">
                  <label htmlFor="description">Description</label>
                  <textarea
                    id="description"
                    name="description"
                    rows={4}
                    placeholder="Describe the scope of this competency area..."
                    value={formData.description}
                    onChange={handleChange}
                  />
                </div>
              </div>

              <div className="scope-toggle-row">
                <button
                  type="button"
                  className={`toggle-switch ${formData.is_active ? "on" : ""}`}
                  onClick={toggleActive}
                  aria-pressed={formData.is_active}
                >
                  <span className="toggle-knob" />
                </button>
                <span className="toggle-label">Active</span>
              </div>

              <div className="scope-form-actions">
                <button
                  type="button"
                  className="cancel-btn"
                  onClick={() => navigate("/catalog/scope-catalogs")}
                  disabled={loading}
                >
                  <FiX /> Cancel
                </button>

                <button type="submit" className="save-btn" disabled={loading}>
                  <FiSave />{" "}
                  {loading
                    ? "Saving..."
                    : isEditMode
                      ? "Update Scope"
                      : "Save Scope"}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AddScopeCatalog;